import type {
	FailedPendingResponse,
	HTTPValidationError,
	PricingCampaignDuplicateRequest,
} from '@/shared/generated'
import type { UseMutationResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { AlertTriangleIcon, CheckCircle2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Spinner } from '@/shared/components/ui/spinner'

type Props = {
	formData: {
		campaigns: string[]
		groups: string[]
	}
	run: UseMutationResult<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignDuplicateRequest
	>
}

export default function ExecutionView({ formData, run }: Props) {
	const { t } = useTranslation()

	if (run.isPending) {
		return (
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<Spinner className='text-muted-foreground' />
					</EmptyMedia>
					<EmptyTitle>{t('pc.processing.loading.title')}</EmptyTitle>
				</EmptyHeader>
			</Empty>
		)
	} else if (run.isError) {
		return (
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon' className='bg-red-500/20'>
						<AlertTriangleIcon className='text-red-500' />
					</EmptyMedia>
					<EmptyTitle>{t('pc.processing.error.title')}</EmptyTitle>
				</EmptyHeader>
			</Empty>
		)
	} else {
		return (
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon' className='bg-green-500/20'>
						<CheckCircle2Icon className='text-green-500' />
					</EmptyMedia>
					<EmptyTitle>
						{t('app.success', { defaultValue: 'Success' })}
					</EmptyTitle>
					<EmptyDescription>
						{t('pc.processing.success.title', {
							action: 'copied',
							campaigns: formData.campaigns.length,
							groups: formData.groups.length,
						})}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}
}
